import { Button } from '@/components/Button';
import { ColorPicker } from '@/components/ColorPicker';
import { QrCodeGenerator } from '@/components/QrCodeGenerator';
import { useQrCode, useQrCodeStore } from '@/hooks/useQrCode';
import { useCardStore } from '@/stores/useCardStore';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload } from 'react-icons/fa6';
import { useParams } from 'react-router-dom';

const QR_CODE_COLORS = [
  { value: '#000000', label: 'Black' },
  { value: '#A7F3D0', label: 'Mint Glass' },
  { value: '#34D399', label: 'Emerald Glass' },
  { value: '#2DD4BF', label: 'Teal Glass' },
  { value: '#22D3EE', label: 'Cyan Glass' },
  { value: '#38BDF8', label: 'Sky Glass' },
  { value: '#818CF8', label: 'Indigo Glass' },
  { value: '#A78BFA', label: 'Violet Glass' },
  { value: '#E879F9', label: 'Fuchsia Glass' },
  { value: '#FB7185', label: 'Rose Glass' },
  { value: '#FBBF24', label: 'Amber Glass' },
];

export const UpdateCardQrCode = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const { updateQrCodeOptions, shareQrCode } = useQrCode();
  const { card, updateCardQrCodeColor } = useCardStore();
  const qrCodeOptions = useQrCodeStore(state => state.qrCodeOptions);
  const [refreshKey, setRefreshKey] = useState(0);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (card) {
      const currentColor = card.qr_code_color || '#000000';
      if (currentColor !== qrCodeOptions.color) {
        updateQrCodeOptions({ color: currentColor });
        setRefreshKey(prev => prev + 1);
      }
    }
  }, [card, qrCodeOptions.color, updateQrCodeOptions]);

  const handleColorChange = async (e: {
    target: { value: string; name: string };
  }) => {
    if (!card || !slug) return;

    try {
      updateQrCodeOptions({ color: e.target.value });
      setRefreshKey(prev => prev + 1);

      await updateCardQrCodeColor(slug, e.target.value);
    } catch (error) {
      const previousColor = card.qr_code_color || '#000000';
      updateQrCodeOptions({ color: previousColor });
      setRefreshKey(prev => prev + 1);
    }
  };

  const handleShare = () => {
    if (!slug || !card || !svgRef.current) return;
    shareQrCode(svgRef.current, card);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        when: 'beforeChildren',
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 400,
        damping: 30,
      },
    },
  };

  if (!slug || !card) {
    return <div>{t('common:notFound')}</div>;
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={containerVariants}>
      <div className="flex flex-col gap-6 md:flex-row">
        {/* QR code preview and share options */}
        <motion.div
          variants={itemVariants}
          className="flex-1 rounded-lg bg-gray-50 p-6"
        >
          <div className="flex flex-col items-center justify-center">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <QrCodeGenerator
                key={refreshKey}
                slug={card.slug}
                qrColor={card.qr_code_color}
                className="size-48"
                svgRef={svgRef}
              />
            </motion.div>

            {/* Share buttons */}
            <motion.div
              variants={itemVariants}
              className="mt-4 flex justify-center"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button onClick={handleShare} className="gap-2">
                  <FaDownload /> {t('common:download')}
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Customization options */}
        <motion.div variants={itemVariants} className="flex-1">
          <ColorPicker
            name="qr_code_color"
            selectedColor={qrCodeOptions.color || '#000000'}
            onChange={handleColorChange}
            label={t('updateCard:qrCode.qrCodeColor')}
            colors={QR_CODE_COLORS}
          />
        </motion.div>
      </div>
    </motion.div>
  );
};
